import { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, Loader2, Download, ExternalLink } from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';
import getRouterBasename from '@/lib/router';
import { TimeFrame } from '@/types/dashboard';
import { TimeRangeSelector } from '@/components/shared/TimerangeSelector';

interface FeedbackEntry {
  id?: string;
  user: string;
  comment: string;
  value: number;
  createdAt: number;
  threadId: string;
  forId?: string;
  assistantName?: string;
  assistantId?: string;
}



const ScoreIcon = ({ value }: { value: FeedbackEntry['value'] }) => {
  if (value === 1) {
    return (
      <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
        <ThumbsUp className="w-4 h-4 text-green-600" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center w-8 h-8 bg-red-100 rounded-full">
      <ThumbsDown className="w-4 h-4 text-red-600" />
    </div>
  );
};

const Header = () => {
  return (
    <div>
      <h2 className="text-3xl font-extrabold text-gray-900">
        Conversations Feedback
      </h2>
      <p className="mt-2 text-gray-600 text-sm">
        User feedback and ratings for conversation quality
      </p>
    </div>
  );
}

const TIME_FRAME_MS: Record<TimeFrame, number> = {
  '1h': 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '90d': 90 * 24 * 60 * 60 * 1000,
};

const getDateRange = (period: TimeFrame): { start: Date; end: Date } => {
  const end = new Date();
  const start = new Date(end.getTime() - TIME_FRAME_MS[period]);
  return { start, end };
};

const downloadCSV = async (
  start: Date,
  end: Date,
  setDownloading: (loading: boolean) => void
) => {
  setDownloading(true);
  try {
    const response = await api.get('/api/v1/reports/feedback_history/download', {
      withCredentials: true,
      responseType: 'blob',
      timeout: 30000,
      params: {
        start: start.toISOString(),
        end: end.toISOString()
      }
    });

    const blob = new Blob([response.data], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = response.headers['x-filename'] || `feedback_export.json`;
    document.body.appendChild(link);

    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
    toast.success('Conversation feedback exported successfully');
  } catch (err) {
    toast.error('Failed to download feedback data');
    console.error('Error downloading feedback data:', err);
  } finally {
    setDownloading(false);
  }
};

export const ConversationsFeedback = () => {
  const [feedbackData, setFeedbackData] = useState<FeedbackEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<TimeFrame>('30d');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchFeedbackData = async () => {
      try {
        setLoading(true);
        setError(null);
        const { start, end } = getDateRange(selectedPeriod);
        const response = await api.get('/api/v1/reports/feedback_history', {
          withCredentials: true,
          params: {
            start: start.toISOString(),
            end: end.toISOString()
          }
        });
        setFeedbackData(response.data);
      } catch (err) {
        setError('Failed to load feedback data');
        console.error('Error fetching feedback data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedbackData();
  }, [selectedPeriod]);

  const formatDate = (date: number) => {
    return new Date(date * 1000).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };


  const handleViewConversation = (threadId: string, forId?: string) => {
    const baseUrl = window.location.origin + getRouterBasename();
    const hash = forId ? `#msg-${forId}` : '';
    const link = `${baseUrl}/share/${encodeURIComponent(threadId)}${hash}`;
    window.open(link, '_blank');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Header />

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-3">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="text-gray-600">Loading feedback data...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Header />

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-red-600 mb-2">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-6 mb-4 md:flex-row md:items-start md:justify-between">
        <div className="flex-1">
          <Header />
        </div>

        <button
          className={`px-4 py-2.5 text-white rounded-xl shadow-sm transition-colors flex items-center gap-2 md:self-end bg-blue-600 ${downloading || feedbackData.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
          name='download-csv'
          disabled={downloading}
          onClick={() => {
            if (feedbackData.length === 0) {
              toast.error('No feedback data available for the selected period', { duration: 2000 });
              setTimeout(() => {
                toast.info('Try selecting a different time range', { duration: 3000 });
              }, 2500);
              return;
            }
            const { start, end } = getDateRange(selectedPeriod);
            downloadCSV(start, end, setDownloading);
          }}
        >
          {downloading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Exporting...
            </>
          ) : feedbackData.length === 0 ? (
            <>
              <Download className="w-4 h-4" />
              No data to export
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              Export to JSON
            </>
          )}
        </button>

        <TimeRangeSelector value={selectedPeriod} onChange={setSelectedPeriod} />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {feedbackData.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-gray-500">No feedback data available</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Assistant
                  </th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Comment
                  </th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">
                    Date
                  </th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    URL
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {feedbackData
                  .sort((a, b) => {
                    const timeDiff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                    if (timeDiff !== 0) return timeDiff;
                    return a.user.localeCompare(b.user);
                  })
                  .map((feedback, index) => (
                    <tr
                      key={feedback.id || index}
                      className={`hover:bg-gray-50/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                        }`}
                    >
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8">
                            <div className="w-7 h-7 md:w-8 md:h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                              <span className="text-[10px] md:text-xs font-medium text-white">
                                {feedback.user.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-2 md:ml-3">
                            <div className="text-xs md:text-sm font-medium text-gray-900 max-w-[80px] md:max-w-none truncate">
                              {feedback.user}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-0.5 md:gap-1 flex-shrink-0 max-w-[90px] md:max-w-none">
                          <span className="text-xs md:text-sm font-medium text-gray-900 truncate" title={feedback?.assistantName}>
                            {feedback?.assistantName || <span className="text-gray-400 italic">Unknown</span>}
                          </span>
                          {feedback?.assistantId && (
                            <span
                              className="hidden md:inline text-[11px] font-mono text-gray-400"
                              title={feedback.assistantId}
                            >
                              <b>ID: </b>
                              {feedback.assistantId.slice(0, 8)}...{feedback.assistantId.slice(-4)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4">
                        <div className="text-xs md:text-sm text-gray-900 max-w-[120px] md:max-w-md truncate md:whitespace-normal" title={feedback.comment !== '---' ? feedback.comment : undefined}>
                          {feedback.comment === '---' ? (
                            <span className="text-gray-400 italic">No comment</span>
                          ) : (
                            <span className="leading-relaxed">{feedback.comment}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-center">
                        <ScoreIcon value={feedback.value} />
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm text-gray-900 hidden sm:table-cell">
                        {formatDate(feedback.createdAt)}
                      </td>
                      <td className="px-2 md:px-6 py-3 md:py-4 whitespace-nowrap text-center">
                        <button
                          className="inline-flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1.5 text-xs font-medium rounded-lg border border-blue-200 bg-blue-50 text-blue-700 transition-all duration-200 hover:bg-blue-100 hover:border-blue-300 hover:shadow-sm"
                          onClick={() => handleViewConversation(feedback.threadId, feedback.forId)}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          <span className="hidden md:inline">View</span>
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
